/**
 * Retry a failed lazy overlay on the shopper's NEXT explicit ask, instead of
 * leaving the boundary parked for the session (PR #1141, COM-189 wave 4
 * follow-up).
 *
 * WHY — release 15 made the login/register drawer (`account-drawer-*.js`),
 * the mobile account menu and the desktop account flyout lazy chunks
 * (COM-189 wave 4/4B). When one of those `import()`s fails (167 beacons for
 * the live drawer hash in the first 14h — flaky mobile networks, content
 * blockers, a challenged asset fetch), `OverlayBoundary` latches to its
 * fallback and every later tap on the account icon or a `#login` link does
 * nothing until the shopper reloads by hand. That reads as "the login button
 * is broken".
 *
 * WHY NOT JUST RE-RENDER THE LAZY COMPONENT — two caches stand in the way:
 * `React.lazy` memoises the rejected import forever, and the HTML module map
 * records a failed module fetch as null, so a second `import()` of the same
 * URL rejects immediately without touching the network (this is why Vite's own
 * guidance for `vite:preloadError` is a reload). A real retry of a module-load
 * failure IS a document reload. Doing it on the shopper's tap (not at failure
 * time — the overlays pre-warm on idle, and reloading a page someone is
 * reading is worse than a dead button) keeps it intentional and bounded.
 *
 * THE PLAN, per failure:
 *   - not a module-load error (a render crash inside the chunk), first time
 *     for this boundary → `remount`: reset the boundary and let the overlay
 *     try again in place;
 *   - not a module-load error, but a remount was already tried and the
 *     overlay failed again (`remountAttempted`) → `navigate`. A chunk that
 *     downloads but throws while evaluating (module-scope TypeError, an HTML
 *     body served as JS, a CSP block) is classified as a render crash yet is
 *     cached by `React.lazy` exactly like a failed import, so a second remount
 *     can never succeed — without this bound a deterministic crash with
 *     `#login` in the URL cycles failure → remount → failure unattended;
 *   - module-load error, build guard not spent → `reload`: mark the build
 *     (same SFCC-11887 key `RootErrorBoundary` and the opt-in
 *     `reloadOnModuleLoadError` lane use, so a chunk that is genuinely gone
 *     can never loop) and reload the document. The URL is kept as-is, so a
 *     `#login` hash re-opens the drawer after the reload. The guard write
 *     happens in `retryOverlay`, right before the reload, and must READ BACK
 *     — a storage that cannot hold the guard cannot bound the reload, so the
 *     act degrades to `navigate` (or `park`, see below);
 *   - module-load error, guard spent or storage blocked → `navigate`: send the
 *     shopper to the full-page equivalent so the tap still lands somewhere
 *     useful. For the drawer that is `/account` (`?action=register` for the
 *     register hash) — see `drawerHashFallbackHref` for why NOT `/login` or
 *     `/register`.
 *
 * UNATTENDED — a caller with no shopper signal (the drawer listener at mount
 * on a hash landing) passes `unattended: true`, and then the ONLY plan that
 * acts is the build-guarded `reload`; everything else is `park` (no beacon, no
 * action — the pre-PR dead drawer until the shopper taps). The owner mounts a
 * fresh listener for every failure, so an unattended `remount` re-feeds the
 * failure in-document, and an unattended `navigate` re-feeds it
 * cross-document: the new document imports the chunk again, it fails again
 * for the same deterministic cause (content blocker, CSP, purged asset), and
 * the fresh listener navigates again — forever, one Worker document render +
 * a `lazy-overlay` beacon per lap. Only `reload` is safe to take without a
 * tap, because the build guard bounds it to once per build per tab.
 *
 * One beacon per retry (`lazy-overlay-retry`, with the action taken) so the
 * recovery rate is countable next to the `lazy-overlay` failure beacons.
 */
import { isSameOriginPath } from "~/lib/utils/auth-links";
import { reportClientError } from "~/utils/report-client-error";

import { parseDrawerHash } from "./account-drawer/drawer-hash";
import {
  MODULE_RELOAD_BUILD_KEY,
  isModuleLoadError,
  readLoadedBuildId,
  shouldReloadForModuleError,
} from "./error-boundries/error-utils";

export type GuardStorage = Pick<Storage, "getItem" | "setItem"> | null;

/** What an `OverlayBoundary` caught, as handed to `onError`. */
export interface OverlayFailure {
  error: unknown;
  moduleLoadError: boolean;
}

export type OverlayRetryPlan =
  | { kind: "remount" }
  | { kind: "reload" }
  | { kind: "navigate"; href: string }
  /** Unattended and the plan was not a guarded reload: do nothing, beacon
   *  nothing; the shopper's next tap plans again with a signal. */
  | { kind: "park" };

export const safeSessionStorage = (): GuardStorage => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * Persist the build-keyed reload guard and confirm it READS BACK. The
 * read-back is load-bearing: a storage whose write silently no-ops (or throws)
 * cannot protect against a reload loop, so callers treat `false` as "do not
 * reload automatically".
 */
export const persistReloadGuard = (
  storage: GuardStorage,
  buildId: string,
): boolean => {
  if (!storage) return false;
  try {
    storage.setItem(MODULE_RELOAD_BUILD_KEY, buildId);
    return storage.getItem(MODULE_RELOAD_BUILD_KEY) === buildId;
  } catch {
    return false;
  }
};

export function classifyOverlayFailure(error: unknown): OverlayFailure {
  return { error, moduleLoadError: isModuleLoadError(error) };
}

export interface OverlayRetryOptions {
  fallbackHref: string;
  /**
   * The caller already remounted this boundary once and it failed again. A
   * render crash then escalates to `navigate` instead of a second remount —
   * see THE PLAN above. Has no effect on a module-load failure (those never
   * remount). Callers reset it when the overlay renders again or the shopper
   * navigates.
   */
  remountAttempted?: boolean;
  /**
   * No shopper signal behind this retry (a hash landing, not a tap). Only a
   * build-guarded `reload` may act; `remount` and `navigate` become `park` —
   * see UNATTENDED above.
   */
  unattended?: boolean;
  buildId?: string;
  storage?: GuardStorage;
}

/**
 * Decide how to retry. PURE — no guard write, so a caller may inspect the plan
 * before deciding to act; `retryOverlay` writes the guard on the `reload`
 * branch only.
 */
export function planOverlayRetry(
  failure: OverlayFailure,
  {
    fallbackHref,
    remountAttempted = false,
    unattended = false,
    buildId = readLoadedBuildId(),
    storage = safeSessionStorage(),
  }: OverlayRetryOptions,
): OverlayRetryPlan {
  const plan = ((): OverlayRetryPlan => {
    if (!failure.moduleLoadError) {
      return remountAttempted
        ? { kind: "navigate", href: fallbackHref }
        : { kind: "remount" };
    }
    // `shouldReloadForModuleError` returns true for a blocked storage (the
    // RootErrorBoundary lane reloads unguarded there); for a tap-driven retry
    // we additionally require a storage the guard can live in, otherwise
    // every tap would be a reload — so a blocked storage falls through to
    // `navigate`.
    const guardFree =
      storage !== null &&
      shouldReloadForModuleError(failure.error, { buildId, storage });
    return guardFree
      ? { kind: "reload" }
      : { kind: "navigate", href: fallbackHref };
  })();
  if (unattended && plan.kind !== "reload") return { kind: "park" };
  return plan;
}

/**
 * Plan, beacon, act. `onRemount` is the caller's reset (bump the boundary's
 * `retryToken` and re-open the overlay); `reload` / `navigate` default to the
 * document APIs and are injectable for tests. A `reload` plan first persists
 * the build guard; if the guard does not read back the reload is unbounded, so
 * it degrades to `navigate` (attended) or `park` (unattended) instead. `park`
 * neither beacons nor acts.
 */
export function retryOverlay(
  overlay: string,
  failure: OverlayFailure,
  {
    fallbackHref,
    remountAttempted,
    unattended = false,
    onRemount,
    reload = () => window.location.reload(),
    navigate = (href: string) => window.location.assign(href),
    buildId = readLoadedBuildId(),
    storage = safeSessionStorage(),
  }: OverlayRetryOptions & {
    onRemount: () => void;
    reload?: () => void;
    navigate?: (href: string) => void;
  },
): OverlayRetryPlan {
  let plan = planOverlayRetry(failure, {
    fallbackHref,
    remountAttempted,
    unattended,
    buildId,
    storage,
  });
  if (plan.kind === "reload" && !persistReloadGuard(storage, buildId)) {
    plan = unattended
      ? { kind: "park" }
      : { kind: "navigate", href: fallbackHref };
  }
  if (plan.kind === "park") return plan;
  // `dedupeKey`: a remount that crashes again escalates with the SAME error,
  // and that second beacon is the one that says the remount failed — it must
  // not be deduped into the first (COM-448).
  reportClientError(
    "lazy-overlay-retry",
    failure.error,
    {
      overlay,
      action: plan.kind,
      moduleLoadError: failure.moduleLoadError,
    },
    { dedupeKey: plan.kind },
  );
  if (plan.kind === "remount") onRemount();
  else if (plan.kind === "reload") reload();
  else navigate(plan.href);
  return plan;
}

/**
 * Full-page equivalent of an auth-drawer hash, for the `navigate` branch:
 * always the `/account` login page (COM-7), with `?action=register` for the
 * `#register` hash so it opens on the Create Account tab (account.tsx honours
 * it, as SFRA's `Login-Show` does). A same-origin `?redirectTo=` on the hash
 * is carried across as a query param, which `/account`'s loader reads through
 * the same `isSameOriginPath` guard every other consumer of an
 * attacker-supplied `redirectTo` applies (it also rejects the `/\host` and
 * control-character spellings browsers fold into `//host`).
 *
 * NOT `/login` or `/register`: both are redirect-only routes. `/register`'s
 * loader 302s a guest straight back to `/#register` — into the drawer that
 * just failed, i.e. a document that imports the chunk again, fails again for
 * the same cause, and would navigate here again, forever. `/login`'s loader
 * 302s to `/account` and DROPS the query, losing the `redirectTo`. Targeting
 * the page that actually renders avoids both.
 *
 * Unknown / absent hash → `/account`.
 */
export function drawerHashFallbackHref(hash: string): string {
  const parsed = parseDrawerHash(hash);
  const params = new URLSearchParams();
  if (parsed?.target === "register") params.set("action", "register");
  const redirectTo = parsed?.redirectTo ?? "";
  if (isSameOriginPath(redirectTo)) params.set("redirectTo", redirectTo);
  const query = params.toString();
  return query ? `/account?${query}` : "/account";
}
